// Keycloak OIDC sign-in for the console SPA — Authorization Code flow with PKCE
// against a PUBLIC client (no client secret in the browser).
//
// This is the client counterpart of the service's `KeycloakAdminAuthenticator`:
// it acquires a Keycloak access token that the service then verifies (RS256 +
// JWKS) and gates on the `iam.admin` scope. It is deliberately dependency-free —
// standard OIDC endpoints + `fetch` + Web Crypto (SubtleCrypto) — so the console
// pulls in no OIDC library and the build/lockfile stay untouched.
//
// Config is injected via Vite env (`VITE_KC_*`). When the issuer is not
// configured the console runs in DEV-STUB mode (see AdminAuthContext) and this
// module is inert — mirroring the service's `AUTH_ADMIN_AUTH_MODE`.

/** Resolved OIDC configuration for the console client. */
export interface OidcConfig {
  /** The Keycloak realm issuer, e.g. `https://auth-staging.solya.app/realms/solya`. */
  issuer: string
  /** The console's public client id, e.g. `solya-iam-console`. */
  clientId: string
  /** Where Keycloak redirects back after login (must be an allowed redirect URI). */
  redirectUri: string
  /** OIDC scopes to request. `openid` is always included. */
  scope: string
}

/** Access token + its absolute expiry (epoch ms) and optional refresh token. */
export interface TokenSet {
  accessToken: string
  /** Absolute expiry (epoch ms) computed from `expires_in`. */
  expiresAt: number
  refreshToken?: string
}

const STORAGE_KEY = "solya.iam.console.tokens"
const PKCE_KEY = "solya.iam.console.pkce"
/** Refresh a little before the real expiry to avoid using an about-to-die token. */
const EXPIRY_SKEW_MS = 30_000

/**
 * The RUNTIME config the service injects via `GET /admin/config.js` (a classic
 * script in index.html that runs before this bundle). See
 * service/src/admin/public-config.ts.
 */
export interface RuntimeConsoleConfig {
  issuer?: string
  clientId?: string
  scope?: string
}

declare global {
  interface Window {
    __SOLYA_IAM_CONFIG__?: RuntimeConsoleConfig | null
  }
}

/**
 * Read the config the SERVICE injected at runtime, or null when absent/incomplete.
 *
 * This takes precedence over the baked `VITE_*` values, and that ordering is the
 * whole point: this repo builds ONE image (CI passes the STAGING issuer as a build
 * arg), so a build-time-only config would send prod administrators to the staging
 * realm. The runtime values come from the pod's env, so one image serves every
 * environment. The service emits this ONLY when it is itself running the real
 * Keycloak authenticator, so the SPA can never advertise a realm the service does
 * not verify against.
 */
export function getRuntimeConfig(): RuntimeConsoleConfig | null {
  const injected = window.__SOLYA_IAM_CONFIG__
  if (!injected) return null
  const issuer = injected.issuer?.trim()
  const clientId = injected.clientId?.trim()
  // Partial runtime config is treated as ABSENT rather than merged with the baked
  // values: a half-runtime/half-build-time config is exactly the cross-environment
  // mismatch this seam exists to prevent.
  if (!issuer || !clientId) return null
  return { issuer, clientId, scope: injected.scope?.trim() || undefined }
}

/**
 * Resolve the console OIDC config. Runtime config (from the service) wins; the
 * baked `VITE_*` values are the fallback for local `vite dev`. Returns null when
 * neither source configures an issuer + client id — the signal to run in dev-stub
 * mode instead of real Keycloak.
 */
export function getOidcConfig(): OidcConfig | null {
  const env = import.meta.env
  const runtime = getRuntimeConfig()

  const issuer = runtime?.issuer ?? env.VITE_KC_ISSUER?.trim()
  const clientId = runtime?.clientId ?? env.VITE_KC_CLIENT_ID?.trim()
  if (!issuer || !clientId) return null

  // The redirect URI is never injected: it is derived from the CURRENT origin, so
  // the same config works on iam-staging.solya.app and iam.solya.app alike.
  const redirectUri = env.VITE_KC_REDIRECT_URI?.trim() || `${window.location.origin}/auth/callback`
  const scope = runtime?.scope ?? (env.VITE_KC_SCOPE?.trim() || "openid profile")
  return { issuer, clientId, redirectUri, scope }
}

// ── OIDC endpoints (Keycloak conventions) ─────────────────────────────────────

function authEndpoint(cfg: OidcConfig): string {
  return `${trimSlash(cfg.issuer)}/protocol/openid-connect/auth`
}
function tokenEndpoint(cfg: OidcConfig): string {
  return `${trimSlash(cfg.issuer)}/protocol/openid-connect/token`
}
function logoutEndpoint(cfg: OidcConfig): string {
  return `${trimSlash(cfg.issuer)}/protocol/openid-connect/logout`
}
function trimSlash(url: string): string {
  return url.replace(/\/+$/, "")
}

// ── PKCE + random helpers (Web Crypto) ────────────────────────────────────────

/** URL-safe base64 (no padding) of raw bytes. */
export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = ""
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

/** A high-entropy URL-safe random string (used for the PKCE verifier + state). */
export function randomUrlToken(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength)
  globalThis.crypto.getRandomValues(bytes)
  return base64UrlEncode(bytes)
}

/** The S256 PKCE code challenge for a verifier. */
export async function pkceChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier)
  const digest = await globalThis.crypto.subtle.digest("SHA-256", data)
  return base64UrlEncode(new Uint8Array(digest))
}

// ── The flow ──────────────────────────────────────────────────────────────────

interface PkceState {
  verifier: string
  state: string
  /** Where to return the user after login completes. */
  returnTo: string
}

/**
 * Begin login: generate PKCE + state, stash them, and redirect the browser to
 * Keycloak's authorization endpoint. Never returns (navigates away).
 */
export async function beginLogin(cfg: OidcConfig): Promise<never> {
  const verifier = randomUrlToken(32)
  const state = randomUrlToken(16)
  const challenge = await pkceChallenge(verifier)

  const pkce: PkceState = {
    verifier,
    state,
    returnTo: window.location.pathname + window.location.search
  }
  sessionStorage.setItem(PKCE_KEY, JSON.stringify(pkce))

  const params = new URLSearchParams({
    client_id: cfg.clientId,
    response_type: "code",
    redirect_uri: cfg.redirectUri,
    scope: cfg.scope,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256"
  })
  window.location.assign(`${authEndpoint(cfg)}?${params.toString()}`)
  // Give TypeScript a `never`: navigation has been requested.
  return new Promise<never>(() => {})
}

/** True when the current URL looks like the OIDC redirect callback (code + state). */
export function isRedirectCallback(): boolean {
  const params = new URLSearchParams(window.location.search)
  return params.has("code") && params.has("state")
}

/**
 * Complete login: exchange the authorization code for tokens (PKCE), persist
 * them, and clean the `?code=...` off the URL. Throws on any mismatch/failure —
 * fail-closed, the caller treats a throw as "not authenticated".
 */
export async function completeLogin(cfg: OidcConfig): Promise<TokenSet> {
  const params = new URLSearchParams(window.location.search)
  const code = params.get("code")
  const returnedState = params.get("state")

  const raw = sessionStorage.getItem(PKCE_KEY)
  sessionStorage.removeItem(PKCE_KEY)
  if (!code || !returnedState || !raw) throw new Error("missing_authorization_code")

  const pkce = JSON.parse(raw) as PkceState
  if (returnedState !== pkce.state) throw new Error("state_mismatch")

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: cfg.clientId,
    code,
    redirect_uri: cfg.redirectUri,
    code_verifier: pkce.verifier
  })

  const tokens = await postToken(cfg, body)
  // Strip the auth params, restoring the pre-login location.
  window.history.replaceState({}, "", pkce.returnTo || "/")
  return tokens
}

/** Exchange a refresh token for a fresh access token; null if it cannot refresh. */
export async function refresh(cfg: OidcConfig, refreshToken: string): Promise<TokenSet | null> {
  try {
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: cfg.clientId,
      refresh_token: refreshToken
    })
    return await postToken(cfg, body)
  } catch {
    return null
  }
}

/** POST the token endpoint (form-encoded) and normalise the response into a TokenSet. */
async function postToken(cfg: OidcConfig, body: URLSearchParams): Promise<TokenSet> {
  const res = await fetch(tokenEndpoint(cfg), {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: body.toString()
  })
  if (!res.ok) throw new Error(`token_endpoint_${res.status}`)

  const json = (await res.json()) as {
    access_token?: string
    refresh_token?: string
    expires_in?: number
  }
  if (!json.access_token) throw new Error("no_access_token")

  const tokens: TokenSet = {
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    expiresAt: Date.now() + (json.expires_in ?? 60) * 1000
  }
  storeTokens(tokens)
  return tokens
}

// ── Token persistence + access ────────────────────────────────────────────────

function storeTokens(tokens: TokenSet): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tokens))
}

/** Load the persisted token set for this tab, if any. */
export function loadTokens(): TokenSet | null {
  const raw = sessionStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as TokenSet
  } catch {
    return null
  }
}

/** Clear all persisted auth state (tokens + any in-flight PKCE). */
export function clearTokens(): void {
  sessionStorage.removeItem(STORAGE_KEY)
  sessionStorage.removeItem(PKCE_KEY)
}

/** True when the token set is absent or within the refresh skew of expiry. */
export function isExpired(tokens: TokenSet | null): boolean {
  if (!tokens) return true
  return Date.now() >= tokens.expiresAt - EXPIRY_SKEW_MS
}

/**
 * Return a usable access token, or null. Resolution order:
 *   1. a persisted, still-valid access token;
 *   2. a refresh using the persisted refresh token;
 *   3. null — the caller should `beginLogin`.
 */
export async function getAccessToken(cfg: OidcConfig): Promise<string | null> {
  const tokens = loadTokens()
  if (tokens && !isExpired(tokens)) return tokens.accessToken
  if (tokens?.refreshToken) {
    const refreshed = await refresh(cfg, tokens.refreshToken)
    if (refreshed) return refreshed.accessToken
  }
  clearTokens()
  return null
}

/** Redirect to Keycloak's end-session endpoint after clearing local tokens. */
export function logout(cfg: OidcConfig): void {
  clearTokens()
  const params = new URLSearchParams({
    client_id: cfg.clientId,
    post_logout_redirect_uri: window.location.origin
  })
  window.location.assign(`${logoutEndpoint(cfg)}?${params.toString()}`)
}
